import {
  ChargeSessionCodeType,
  CodeSearchType,
  CodeType,
  CustomerPhase,
  uiStatus
} from '../../enums';
import {
  CaseStatusDisplayName,
  DescriptionDisplayValues,
  ReasonDictionary
} from './case-status-strings';
import { uuidv4 } from '../artifacts/uuidv4';

type ReasonResponse = string | boolean;

const UIStatus = uiStatus;

export const getCaseRuns = (detail: CMxAPI.Case, runType: string) => {
  const caseRuns = detail?.caseRuns || [];
  if (!caseRuns.length) {
    throw new Error('no case runs available');
  }
  const filteredCaseRuns = caseRuns.filter(run => run.run_type === runType);
  return filteredCaseRuns;
};

export const getCaseRun = (detail: CMxAPI.Case, activeProcessId = -1) => {
  const caseRuns = detail?.caseRuns || [];
  if (!caseRuns.length) {
    throw new Error('no case runs available');
  }

  if (activeProcessId === -1) {
    const primaryRun = caseRuns.find(run => run.primary_process_indicator);
    return primaryRun || detail.caseRuns[0];
  }

  const activeCaseRun = caseRuns.find(
    run => run.process_instance_id === activeProcessId
  );
  return activeCaseRun || detail.caseRuns[0];
};

export const deriveStatus = (
  caseRun: CMxAPI.CaseRun | CMxAPI.RelatedCase
): CMxCommonApp.StatusDisplayValues => {
  let caseStatus: string;
  let automationIndicator = false;

  if (!caseRun) {
    caseStatus = UIStatus.DORMANT;
  } else {
    caseStatus = caseRun.case_status;
    automationIndicator = caseRun.automated_indicator;
  }

  const status: CMxCommonApp.StatusIndication = {
    automated: automationIndicator,
    inProcess: false,
    sentToCac: false,
    sentToBill: false
  };

  const { process_status } = caseRun;

  const statusReadout: CMxCommonApp.StatusDisplayValues = {
    description: 'Unspecified',
    signifier: 'Unspecified',
    styleClass: '',
    processStatus: process_status,
    status
  };

  // ---------------- in progress --------------------- //
  if (caseStatus === UIStatus.IN_PROGRESS) {
    if (process_status === `At Coder`) {
      statusReadout.description = `Waiting For Coder`;
      statusReadout.signifier = UIStatus.WAITING;
      statusReadout.styleClass = 'case-status-waiting';
    } else {
      statusReadout.description = `In Progress`;
      statusReadout.signifier = UIStatus.IN_PROGRESS;
      statusReadout.styleClass = 'case-status-in-progress';
    }
    status.inProcess = true;

    return { ...statusReadout, status };
  }
  // Everything else is not in progress.  Which means that it's either cancelled, complete, or in error.

  // ---------------- cancelled --------------------- //
  if (caseStatus === UIStatus.CANCELLED) {
    statusReadout.signifier = CaseStatusDisplayName.Cancelled;
    statusReadout.description = DescriptionDisplayValues.Cancelled;
    statusReadout.styleClass = 'case-status-cancelled';
    return { ...statusReadout, status };
  }

  if (caseStatus === UIStatus.CLOSED_NON_CHARGEABLE) {
    statusReadout.signifier = CaseStatusDisplayName.CLOSED_NON_CHARGEABLE;
    statusReadout.description = DescriptionDisplayValues.CLOSED_NON_CHARGEABLE;
    statusReadout.styleClass = 'case-status-cancelled';
    return { ...statusReadout, status };
  }

  // ---------------- completed --------------------- //
  if (caseStatus === UIStatus.DORMANT) {
    statusReadout.signifier = CaseStatusDisplayName.Dormant;
    statusReadout.description = CaseStatusDisplayName.Dormant;
    statusReadout.styleClass = 'case-status-historical-above';

    return { ...statusReadout, status };
  }

  // CAC
  if (caseStatus === UIStatus.MANUAL) {
    statusReadout.signifier = CaseStatusDisplayName.CAC;
    statusReadout.description = DescriptionDisplayValues.CAC;
    statusReadout.styleClass = 'case-status-cac';
    status.sentToCac = true;
    return { ...statusReadout, status };
  }

  // Updated to Manual (CAC)
  if (caseStatus === UIStatus.UPDATED_MANUAL) {
    statusReadout.signifier = CaseStatusDisplayName.Updated_Manual;
    statusReadout.description = DescriptionDisplayValues.Updated_Manual;
    statusReadout.styleClass = 'case-status-updated-to-manual';
    status.sentToCac = true;
    return { ...statusReadout, status };
  }

  // Sent to Billing
  if (caseStatus === UIStatus.AUTOMATED) {
    statusReadout.signifier = CaseStatusDisplayName.Automated;
    statusReadout.description = DescriptionDisplayValues.Automated;
    statusReadout.styleClass = 'case-status-sent-to-bill';
    status.sentToBill = true;
    return { ...statusReadout, status };
  }

  // ---------------- Fall through  --------------------- //
  return { ...statusReadout, status };
};

const terminalStatus = new Set<string>([
  uiStatus.CANCELLED,
  uiStatus.AUTOMATED,
  uiStatus.MANUAL
]);

export const isFinishedCase = (
  caseRun: CMxAPI.CaseRun | CMxAPI.RelatedCase
) => {
  if (caseRun === undefined) {
    return false;
  }
  return terminalStatus.has(caseRun.case_status);
};

export const isPreGoLive = (caseRun: CMxAPI.CaseRun) => {
  if (caseRun === undefined) {
    return false;
  }
  const phaseName = (caseRun.phase ?? '').toLowerCase();
  return (
    phaseName !== CustomerPhase.LIVE.toLowerCase() &&
    phaseName !== CustomerPhase.PRODUCTION.toLowerCase()
  );
};

export const getStatusReason = (
  caseRun: CMxAPI.CaseRun | CMxAPI.RelatedCase
): ReasonResponse => {
  if (caseRun === undefined) {
    return false;
  }

  // if something isn't in a terminal state, then it doesn't make
  // sense to look for a reason we made a decision on something.
  if (!isFinishedCase(caseRun)) {
    return caseRun.process_status;
  }

  if (caseRun.case_status === UIStatus.MANUAL) {
    // vitalware reasons short circuit the rest of reason processing.
    const failedEdits = caseRun.claims_edit_indicator === false;
    if (failedEdits) {
      return 'Due to Claim Edits';
    }
    if (caseRun.cci_edit_indicator === false) {
      return 'Due to CCI Edits';
    }
  }

  // if there's a post-prediction reason so that should be given primacy over
  // a pre-prediction.  This will need to be expanded
  const reason =
    caseRun.post_prediction_process_reason ||
    caseRun.pre_prediction_process_reason;

  if (reason === null || caseRun.rules === null) {
    return false;
  }

  let rules: CMxAPI.RuleDecision;
  try {
    rules = JSON.parse(caseRun.rules);
  } catch (e) {
    // TORES: PBC
    // need to validate if we want to do anything more sophisticated here.
    return reason;
  }

  const [category, subCategory] = reason.split(':');
  if (!rules[category]) {
    return reason;
  }

  const activeDecisions: CMxAPI.RuleDescription[] = rules[category];
  if (!activeDecisions || activeDecisions.length === 0) {
    return reason;
  }

  const reasons = activeDecisions
    .filter(
      decision =>
        decision.decoration_subcategory === subCategory ||
        decision.decoration_subcategory.indexOf(subCategory) !== -1
    )
    .map(
      decision =>
        ReasonDictionary[decision.decoration_description] ||
        decision.decoration_description
    );

  const finalReason = reasons.length ? reasons.join(', ') : reason;
  return `${finalReason}`;
};

const excludeActiveCaseRun = (
  activeCase: CMxAPI.CaseRun,
  billingGroup: CMxAPI.Case[]
) => {
  const allRuns = billingGroup.map(getCaseRun);
  return allRuns.filter(caseRun => {
    return (
      caseRun.process_instance_id.toString() !==
      activeCase.process_instance_id.toString()
    );
  });
};

export const getGroupedCases = (
  activeCase: CMxAPI.CaseRun,
  groupedCases: CMxAPI.Case[],
  groupType: string
) => {
  return {
    groupType,
    caseRuns: excludeActiveCaseRun(activeCase, groupedCases)
  };
};

export const getCaseLink = (
  case_uid: string,
  case_run_uid: string,
  service_line: string
) => {
  return !!case_uid && !!case_run_uid && !!service_line
    ? `${service_line}/case/${case_uid}/case-run/${case_run_uid}/`
    : '';
};

export const getChildWindowParams = (
  case_uid: string,
  case_run_uid: string,
  service_line: string,
  windowOpenArgs: CMxCommonApp.WindowOpenArgBase,
  case_id?: string | number
): CMxCommonApp.WindowOpenArguments => {
  // For Pathology cases that don't have case_uid, use case_id
  let effectiveCaseId = case_uid;
  if (!case_uid && case_id && service_line?.toUpperCase() === 'PATHOLOGY') {
    effectiveCaseId = String(case_id);
  }
  
  if (effectiveCaseId && case_run_uid) {
    const params = {
      launchpoint: 'Amplify',
      windowNameExtension: `${effectiveCaseId}_${case_run_uid}`,
      managed: 'false'
    };
    const caseLink = getCaseLink(effectiveCaseId, case_run_uid, service_line);
    const base = {
      ...windowOpenArgs,
      url: `${windowOpenArgs.url}${caseLink}`
    };

    return { ...base, params };
  }
  return null;
};

export const getCodeTypeShortName = (codeType: string) => {
  if (!codeType) return;
  const codeTypeMap = {
    [CodeType.CPT.toUpperCase()]: CodeSearchType.CPT,
    [CodeType.PROCEDURE.toUpperCase()]: CodeSearchType.CPT,
    [CodeType.MODIFIER.toUpperCase()]: CodeSearchType.MODIFIER,
    [CodeType.DIAGNOSIS.toUpperCase()]: CodeSearchType.ICD,
    [CodeType.ICD.toUpperCase()]: CodeSearchType.ICD
  };
  return codeTypeMap[codeType.toUpperCase()];
};

export const getCodeTypeFullname = (codeType: string) => {
  if (!codeType) return;
  const codeTypeMap = {
    [CodeType.CPT.toUpperCase()]: ChargeSessionCodeType.PROCEDURE,
    [CodeType.PROCEDURE.toUpperCase()]: ChargeSessionCodeType.PROCEDURE,
    [CodeType.MODIFIER.toUpperCase()]: ChargeSessionCodeType.MODIFIER,
    [CodeType.DIAGNOSIS.toUpperCase()]: ChargeSessionCodeType.DIAGNOSIS,
    [CodeType.ICD.toUpperCase()]: ChargeSessionCodeType.DIAGNOSIS
  };
  return codeTypeMap[codeType?.toUpperCase()];
};
